import { type UserVideo } from '@/api/videoApi';
import { UserImage } from '@/api/imageApi';
import { downloadImageFromUrl } from '../common/imageUtils';
import { downloadVideoFromUrl } from '../common/videoUtils';

// Re-export for backward compatibility
export { renderContentPreview } from '../common/modal/contentRenderUtils';

/**
 * Submits a video delete request and updates state
 */
export const submitVideoDeleteRequest = async (
  onRequestDelete: ((videoId: string) => Promise<void>) | undefined,
  selectedVideo: UserVideo | null | undefined,
  setHasDeleteRequest: (value: boolean) => void
): Promise<void> => {
  if (onRequestDelete && selectedVideo) {
    await onRequestDelete(selectedVideo.id);
    setHasDeleteRequest(true);
  }
};

/**
 * Submits an image delete request and updates state
 */
export const submitImageDeleteRequest = async (
  onRequestImageDelete: ((imageId: string) => Promise<void>) | undefined,
  selectedImage: UserImage | null | undefined,
  setHasDeleteRequest: (value: boolean) => void
): Promise<void> => {
  if (onRequestImageDelete && selectedImage) {
    await onRequestImageDelete(selectedImage.id);
    setHasDeleteRequest(true);
  }
};

/**
 * Gets the appropriate delete button title based on user role and request status
 */
export const getDeleteButtonTitle = (isAdmin: boolean, hasDeleteRequest: boolean): string => {
  if (isAdmin) return "Delete";
  if (hasDeleteRequest) return "Delete request already submitted";
  return "Request Deletion";
};

/**
 * Gets the appropriate delete confirmation message based on user role and request status
 */
export const getDeleteConfirmationMessage = (isAdmin: boolean, hasDeleteRequest: boolean): string => {
  if (!isAdmin && hasDeleteRequest) {
    return 'Delete request has already been submitted.';
  }
  if (isAdmin) {
    return 'Are you sure you want to confirm deletion?';
  }
  return 'Are you sure you want to submit delete request?';
};

/**
 * Handles delete action for admin users
 */
export const handleAdminDelete = async (
  isVideoContent: boolean,
  isImageContent: boolean,
  onDelete: ((videoId: string) => Promise<void>) | undefined,
  onImageDelete: ((imageId: string) => Promise<void>) | undefined,
  selectedVideo: UserVideo | null | undefined,
  selectedImage: UserImage | null | undefined
): Promise<void> => {
  try {
    if (isVideoContent && onDelete && selectedVideo) {
      await onDelete(selectedVideo.id);
    } else if (isImageContent && onImageDelete && selectedImage) {
      await onImageDelete(selectedImage.id);
    }
  } catch (error) {
    console.error(`Failed to delete ${isVideoContent ? 'video' : 'image'}:`, error);
    throw error;
  }
};

/**
 * Handles video delete request submission for non-admin users
 */
export const handleVideoDeleteRequest = async (
  onRequestDelete: ((videoId: string) => Promise<void>) | undefined,
  selectedVideo: UserVideo | null | undefined,
  setHasDeleteRequest: (value: boolean) => void
): Promise<void> => {
  await submitVideoDeleteRequest(onRequestDelete, selectedVideo, setHasDeleteRequest);
};

/**
 * Handles image delete request submission for non-admin users
 */
export const handleImageDeleteRequest = async (
  onRequestImageDelete: ((imageId: string) => Promise<void>) | undefined,
  selectedImage: UserImage | null | undefined,
  setHasDeleteRequest: (value: boolean) => void
): Promise<void> => {
  await submitImageDeleteRequest(onRequestImageDelete, selectedImage, setHasDeleteRequest);
};

/**
 * Handles downloading last frame for videos
 */
export const handleDownloadVideoFrame = async (
  selectedVideo: UserVideo | null | undefined,
  onDownloadLastFrame: ((signedUrl: string) => Promise<void>) | undefined
): Promise<void> => {
  if (selectedVideo && onDownloadLastFrame) {
    await onDownloadLastFrame(selectedVideo.video_url);
  }
};

/**
 * Handles downloading image
 */
export const handleDownloadImage = async (
  selectedImage: UserImage | null | undefined
): Promise<void> => {
  if (selectedImage) {
    const filename = selectedImage.file_name || `image_${selectedImage.id}.jpg`;
    await downloadImageFromUrl(selectedImage.image_url, filename);
  }
};

/**
 * Handles downloading video file
 */
export const handleDownloadVideo = async (
  selectedVideo: UserVideo | null | undefined
): Promise<void> => {
  if (!selectedVideo) return;
  
  const filename = (selectedVideo.title || `video_${selectedVideo.id}`) + '.mp4';
  await downloadVideoFromUrl(selectedVideo.id, filename);
};

